import React, { useCallback, useState, useEffect } from "react";
import { useIsMobile } from "../../custom-hooks/useIsMobile";
import { StyleLinks } from "../../orgViews/OrgCommonComponents";
import { FaHome, FaCog, FaChartLine, FaShoppingCart, FaStore, FaUsers, FaMoneyBillWave, FaClock, FaFileInvoiceDollar, FaHandPointer, FaPowerOff, FaBars } from 'react-icons/fa';
import './workynavigation.css';
import AddCircleRoundedIcon from '@mui/icons-material/AddCircleRounded';
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange';
import BusinessIcon from '@mui/icons-material/Business';
import {
    Star,
    Rocket,
    LogOut,
    Plus,
    Clock,
    Users,
    CreditCard,
    FileText,
    Timer,
    Zap,
    UserPlus,
    Store,
    ChartGantt,
    PanelLeftOpen,
    PanelLeftClose,
    Cog,
    Home,
    Briefcase,
    TrendingUp,
    Sparkles,
    Settings,
    X,
    Settings2,
    ChevronDown,
    ChevronRight,
    ChevronLeft,
    CircleUserRound,
    CircleDollarSign,
    House,
    CheckSquare,
    SquarePen,
    BarChart3,
    Package,
    ClipboardList,
} from 'lucide-react';
import { Chip, Tooltip, Modal } from "@mui/material";
import GlobalSearch from './GlobalSearch';
import { GetCorpDetailsZip } from "../../shared-engineering-services/SharedAPICalls";
import { CorpDetailsCorpZip } from "../../orgViews/orgConstants";
import { getSessionStorageValue, handleSignOut, LOCAL_STORAGE_KEYS, RetrieveFromLocalCache, setLocalStorageValue } from "../../common/CommonUtilsMethods";
import { CreateWorkyTasks } from "../../worky-leads/components/CreateWorkyTasks";
import { WORKY_WEB_APP_PATHS } from "../../WorkyWebAppPaths";

const NAV_SECTION_EXECUTION_TITLE: string = 'EXECUTION';
const NAV_SECTION_EXECUTION_TITLE_COLLAPSED: string = 'EXE';
const NAV_SECTION_BILLING_PAY_TITLE: string = 'BILLING & PAY';
const NAV_SECTION_BILLING_PAY_TITLE_COLLAPSED: string = 'FIN';

const isCustomReportsNavPathActive = (url: string): boolean =>
    url.includes(WORKY_WEB_APP_PATHS.agencyBillingPayReporting)
    || url.includes(WORKY_WEB_APP_PATHS.agencyBillingPayReportingShiftRecon)
    || url.includes(WORKY_WEB_APP_PATHS.agencyBillingPayReportingTimeClock);

interface WorkyNavigationProps {
    readonly showTopHeader?: boolean | null;
}

const WorkyNavigation = ({ showTopHeader }: WorkyNavigationProps) => {
    const isMobile = useIsMobile();
    const [showSalesMenu, setShowSalesMenu] = useState(false);
    const [showOperationsMenu, setShowOperationsMenu] = useState(false);
    const [isCollapsed, setIsCollapsed] = useState(() => {
        const savedState: string | null = RetrieveFromLocalCache(LOCAL_STORAGE_KEYS.WORKY_NAVIGATION_COLLAPSED);
        return savedState ? JSON.parse(savedState) : false;
    });
    const [showAccountMenu, setShowAccountMenu] = useState(false);
    const [corpDetails, setCorpDetails] = useState<CorpDetailsCorpZip | null>(null);
    const [isExecutionSectionExpanded, setIsExecutionSectionExpanded] = useState(() => {
        const savedState: string | null = RetrieveFromLocalCache(LOCAL_STORAGE_KEYS.WORKY_NAVIGATION_EXECUTION_EXPANDED);
        return savedState ? JSON.parse(savedState) : true;
    });
    const [isFinanceSectionExpanded, setIsFinanceSectionExpanded] = useState(() => {
        const savedState: string | null = RetrieveFromLocalCache(LOCAL_STORAGE_KEYS.WORKY_NAVIGATION_FINANCE_EXPANDED);
        return savedState ? JSON.parse(savedState) : true;
    });
    const [isSalesSectionExpanded, setIsSalesSectionExpanded] = useState(() => {
        const savedState: string | null = RetrieveFromLocalCache(LOCAL_STORAGE_KEYS.WORKY_NAVIGATION_SALES_EXPANDED);
        return savedState ? JSON.parse(savedState) : true;
    });
    const [showCreateTaskPopper, setShowCreateTaskPopper] = useState(false);
    const [showQuickActionsMenu, setShowQuickActionsMenu] = useState(false);
    const url = window.location.href;

    const [localFirstName, setLocalFirstName] = useState(getSessionStorageValue({ key: LOCAL_STORAGE_KEYS.FIRST_NAME }) || '');
    const [localLastName, setLocalLastName] = useState(getSessionStorageValue({ key: LOCAL_STORAGE_KEYS.LAST_NAME }) || '');

    useEffect(() => {
        const handleStorageChange = (e: StorageEvent) => {
            switch (e.key) {
                case 'firstName':
                    setLocalFirstName(e.newValue || '');
                    break;
                case 'lastName':
                    setLocalLastName(e.newValue || '');
                    break;
                default:
                    break;
            }
        };

        const checkInitialValues = () => {
            const currentFirstName = getSessionStorageValue({ key: LOCAL_STORAGE_KEYS.FIRST_NAME });
            const currentLastName = getSessionStorageValue({ key: LOCAL_STORAGE_KEYS.LAST_NAME });

            if (currentFirstName && currentFirstName !== localFirstName) {
                setLocalFirstName(currentFirstName);
            }
            if (currentLastName && currentLastName !== localLastName) {
                setLocalLastName(currentLastName);
            }
        };

        checkInitialValues();
        window.addEventListener('storage', handleStorageChange);

        return () => {
            window.removeEventListener('storage', handleStorageChange);
        };
    }, [localFirstName, localLastName]);

    // Call GetCorpDetailsZip on component mount
    useEffect(() => {
        const fetchCorpDetails = async () => {
            try {
                const details = await GetCorpDetailsZip();
                setCorpDetails(details);
            } catch (error) {
                // Silently handle the exception as requested
                console.error('Failed to fetch corp details:', error);
            }
        };

        fetchCorpDetails();
    }, []);

    const handleProfileIconClick = () => setShowAccountMenu(prev => !prev);

    const handleAccountMenuClose = () => {
        setShowAccountMenu(false);
    };

    const handleHomeClick = () => { window.location.assign('/homepage') }
    const handleOperationsHomeClick = () => { window.location.assign('/operations-homepage') }
    const handlePlannedShiftsClick = () => { window.location.assign('/my-shifts') }
    const handleNewShiftClick = () => { window.location.assign('/new-shift') }
    const handlePaymentsClick = () => { window.location.assign('/agency/payments') }
    const handleAccountSettingsClick = () => { window.location.assign('/account') }
    const handleLogOutClick = () => { handleSignOut() }
    const handleTimePortalClick = () => { window.location.assign('/agency/worky-time-clock') }
    const handleWorkforceClick = () => { window.location.assign('/agency/workforce') }
    const handleWorkyPayClick = () => { window.location.assign('/agency/worky-pay') }
    const handleWorkyBillingClick = () => { window.location.assign('/agency/clients') }
    const handleCatalogItemsClick = () => { window.location.assign('/agency/catalog-items') }
    const handleTasksClick = () => { window.location.assign(WORKY_WEB_APP_PATHS.agencyTasks) }
    const handleClientInvoicesClick = () => { window.location.assign('/agency/client-invoices') }
    const handleBillingPayReportingClick = () => { window.location.assign(WORKY_WEB_APP_PATHS.agencyBillingPayReporting) }
    const handleWorkyLeadsHomeClick = () => { window.location.assign('/agency/sales/home') }
    const handleWorkyLeadsClick = () => { window.location.assign('/agency/leads') }
    const handleWorkyActionsClick = () => { window.location.assign('/agency/actions') }
    const handleCustomActionsClick = () => { window.location.assign('/agency/custom-actions') }
    const handleWorkyMarketplaceClick = () => { window.location.assign('/agency/marketplace') }
    const handleViewMyTasksClick = () => { window.location.assign('/agency/view-tasks') }
    const handleAddNewContactClick = () => { window.location.assign('/agency/create-new-contact') }

    const handleCreateTaskClick = () => {
        setShowCreateTaskPopper(prev => !prev);
    };

    const handleCreateTaskPopperClose = () => {
        setShowCreateTaskPopper(false);
    };

    // Close menus when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as HTMLElement;
            if (!target.closest('.worky-navigation-menu') && !target.closest('.nav-button')) {
                setShowSalesMenu(false);
                setShowOperationsMenu(false);
                setShowQuickActionsMenu(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Close menus when navigating
    useEffect(() => {
        setShowSalesMenu(false);
        setShowOperationsMenu(false);
        setShowQuickActionsMenu(false);
    }, [url]);

    // Toggle menus with mutual exclusivity
    const toggleSalesMenu = (e: React.MouseEvent) => {
        e.stopPropagation();
        setShowSalesMenu(prev => !prev);
        setShowOperationsMenu(false);
        setShowQuickActionsMenu(false);
    };

    const toggleOperationsMenu = (e: React.MouseEvent) => {
        e.stopPropagation();
        setShowOperationsMenu(prev => !prev);
        setShowSalesMenu(false);
        setShowQuickActionsMenu(false);
    };

    const toggleQuickActionsMenu = (e: React.MouseEvent) => {
        e.stopPropagation();
        setShowQuickActionsMenu(prev => !prev);
        setShowSalesMenu(false);
        setShowOperationsMenu(false);
    };

    const toggleSidebar = () => {
        setIsCollapsed((prev: boolean) => {
            const newState: boolean = !prev;
            setLocalStorageValue({ key: LOCAL_STORAGE_KEYS.WORKY_NAVIGATION_COLLAPSED, value: JSON.stringify(newState) });
            return newState;
        });
    };

    const toggleExecutionSection = () => {
        setIsExecutionSectionExpanded((prev: boolean) => {
            const newState: boolean = !prev;
            setLocalStorageValue({ key: LOCAL_STORAGE_KEYS.WORKY_NAVIGATION_EXECUTION_EXPANDED, value: JSON.stringify(newState) });
            return newState;
        });
    };

    const toggleFinanceSection = () => {
        setIsFinanceSectionExpanded((prev: boolean) => {
            const newState: boolean = !prev;
            setLocalStorageValue({ key: LOCAL_STORAGE_KEYS.WORKY_NAVIGATION_FINANCE_EXPANDED, value: JSON.stringify(newState) });
            return newState;
        });
    };

    const toggleSalesSection = () => {
        setIsSalesSectionExpanded((prev: boolean) => {
            const newState: boolean = !prev;
            setLocalStorageValue({ key: LOCAL_STORAGE_KEYS.WORKY_NAVIGATION_SALES_EXPANDED, value: JSON.stringify(newState) });
            return newState;
        });
    };

    useEffect(() => {
        // Add or remove a class on the body based on the collapsed state
        if (isCollapsed) {
            document.body.classList.add('navigation-collapsed');
        } else {
            document.body.classList.remove('navigation-collapsed');
        }
        // Cleanup function to remove the class when the component unmounts or isCollapsed changes
        return () => {
            document.body.classList.remove('navigation-collapsed');
        };
    }, [isCollapsed]);

    const WorkyNavigationMobile = () => {
        return (
            <>
                <div className="worky-navigation-mobile">
                    <button onClick={handleHomeClick} className={`nav-button ${url.includes('homepage') ? 'active' : ''}`}>
                        <Home className="nav-icon" size={20} />
                        <span>Home</span>
                    </button>

                    <button
                        onClick={toggleOperationsMenu}
                        className={`nav-button ${(url.includes('/new-shift') || url.includes('/my-shifts') ||
                            url.includes('/agency/workforce') || url.includes('/agency/attributes') || url.includes('/agency/worky-pay') ||
                            url.includes('/agency/clients') || url.includes(WORKY_WEB_APP_PATHS.agencyCareProfileAttributes) || url.includes('/agency/catalog-items') ||
                            url.includes(WORKY_WEB_APP_PATHS.agencyTasks) ||
                            url.includes('/agency/client-invoices') ||
                            isCustomReportsNavPathActive(url) ||
                            url.includes('/agency/worky-time-clock')) ? 'active' : ''}`}
                    >
                        <Briefcase className="nav-icon" size={20} />
                        <span>Ops</span>
                    </button>

                    <button
                        onClick={toggleSalesMenu}
                        className={`nav-button ${(url.includes('/agency/leads') || url.includes('/agency/marketplace') || url.includes('/agency/view-tasks') || url.includes('/agency/create-new-contact')) ? 'active' : ''}`}
                    >
                        <CircleDollarSign className="nav-icon" size={20} />
                        <span>Sales</span>
                    </button>

                    <button 
                        onClick={toggleQuickActionsMenu} 
                        className={`nav-button ${showQuickActionsMenu ? 'active' : ''}`}
                    >
                        <Zap className="nav-icon" size={20} />
                        <span>Quick Actions</span>
                    </button>
                </div>

                {/* Sales Menu */}
                {showSalesMenu && (
                    <div className="worky-navigation-menu">
                        <div className="worky-navigation-menu-content">
                            <button
                                onClick={handleWorkyLeadsClick}
                                className={`menu-item ${url.includes('/agency/sales/home') ? 'active' : ''}`}
                            >
                                <House className="menu-icon" size={20} />
                                <span>Home</span>
                            </button>
                            <button
                                onClick={handleWorkyLeadsClick}
                                className={`menu-item ${url.includes('/agency/leads') ? 'active' : ''}`}
                            >
                                <CurrencyExchangeIcon className="menu-icon" sx={{ fontSize: "20px" }} />
                                <span>Leads Pro</span>
                            </button>
                            <button
                                onClick={handleAddNewContactClick}
                                className={`menu-item ${url.includes('/agency/create-new-contact') ? 'active' : ''}`}
                            >
                                <UserPlus className="menu-icon" size={20} />
                                <span>Add New Contact</span>
                            </button>
                            <button
                                // onClick={handleWorkyMarketplaceClick} 
                                className={`menu-item ${url.includes('/agency/marketplace') ? 'active' : ''}`}
                            >
                                <Store className="menu-icon" size={20} />
                                <span>Marketplace</span>
                                <Chip label="In Review" color="default" size="small" style={{ marginLeft: "10px" }} />
                            </button>
                            <button
                                className="menu-item worky-ai-menu-item"
                            >
                                <Sparkles className="menu-icon" size={20} />
                                <span>Worky AI</span>
                                <Chip label="Coming Soon!" color="primary" size="small" style={{ marginLeft: "10px" }} />
                            </button>
                            <button
                                onClick={handleViewMyTasksClick}
                                className={`menu-item ${url.includes('/agency/view-tasks') ? 'active' : ''}`}
                            >
                                <CheckSquare className="menu-icon" size={20} />
                                <span>View Tasks</span>
                            </button>
                        </div>
                    </div>
                )}

                {/* Operations Menu (Execution + Billing & Pay) */}
                {showOperationsMenu && (
                    <div className="worky-navigation-menu">
                        <div className="worky-navigation-menu-content">
                            <div className="worky-navigation-mobile-menu-section-label">{NAV_SECTION_EXECUTION_TITLE}</div>
                            <button
                                onClick={handleNewShiftClick}
                                className={`menu-item ${url.includes('/new-shift') ? 'active' : ''}`}
                            >
                                <Plus className="menu-icon" size={20} />
                                <span>New Shift</span>
                            </button>
                            <button
                                onClick={handlePlannedShiftsClick}
                                className={`menu-item ${url.includes('/my-shifts') ? 'active' : ''}`}
                            >
                                <ChartGantt className="menu-icon" size={20} />
                                <span>Shift Management</span>
                            </button>
                            <button
                                onClick={handleWorkforceClick}
                                className={`menu-item ${url.includes('/agency/workforce') || url.includes('/agency/attributes') ? 'active' : ''}`}
                            >
                                <Users className="menu-icon" size={20} />
                                <span>Workforce</span>
                            </button>
                            <button
                                onClick={handleWorkyBillingClick}
                                className={`menu-item ${url.includes('/agency/clients') || url.includes(WORKY_WEB_APP_PATHS.agencyCareProfileAttributes) ? 'active' : ''}`}
                            >
                                <BusinessIcon className="menu-icon" sx={{ fontSize: "20px" }} />
                                <span>Client Management</span>
                            </button>
                            <button
                                onClick={handleCatalogItemsClick}
                                className={`menu-item ${url.includes('/agency/catalog-items') ? 'active' : ''}`}
                            >
                                <Package className="menu-icon" size={20} />
                                <span>Catalog items</span>
                            </button>
                            <button
                                onClick={handleTasksClick}
                                className={`menu-item ${url.includes(WORKY_WEB_APP_PATHS.agencyTasks) ? 'active' : ''}`}
                            >
                                <ClipboardList className="menu-icon" size={20} />
                                <span>Trust Vault</span>
                            </button>
                            <div className="worky-navigation-mobile-menu-section-label worky-navigation-mobile-menu-section-label-spaced">{NAV_SECTION_BILLING_PAY_TITLE}</div>
                            <button
                                onClick={handleWorkyPayClick}
                                className={`menu-item ${url.includes('/agency/worky-pay') ? 'active' : ''}`}
                            >
                                <CreditCard className="menu-icon" size={20} />
                                <span>Worky Pay</span>
                            </button>
                            <button
                                onClick={handleClientInvoicesClick}
                                className={`menu-item ${url.includes('/agency/client-invoices') ? 'active' : ''}`}
                            >
                                <FileText className="menu-icon" size={20} />
                                <span>Client Invoices</span>
                            </button>
                            <button
                                onClick={handleBillingPayReportingClick}
                                className={`menu-item ${isCustomReportsNavPathActive(url) ? 'active' : ''}`}
                            >
                                <BarChart3 className="menu-icon" size={20} />
                                <span>Reporting</span>
                            </button>
                            {/*
                            <button
                                onClick={handleTimePortalClick}
                                className={`menu-item ${url.includes('/agency/worky-time-clock') ? 'active' : ''}`}
                            >
                                <Timer className="menu-icon" size={20} />
                                <span>Time Clock</span>
                            </button>
                            */}
                            {/* <button
                                onClick={handleWorkyActionsClick}
                                className={`menu-item ${url.includes('/agency/actions') ? 'active' : ''}`}
                            >
                                <Zap className="menu-icon" size={20} />
                                <span>Agency Setup</span>
                            </button> */}
                            {/* <button 
                                onClick={handleCustomActionsClick} 
                                className={`menu-item ${url.includes('/agency/custom-actions') ? 'active' : ''}`}
                            >
                                <Settings2 className="menu-icon" size={20} />
                                <span>Custom Actions</span>
                            </button> */}
                        </div>
                    </div>
                )}

                {/* Quick Actions Menu */}
                {showQuickActionsMenu && (
                    <div className="worky-navigation-menu">
                        <div className="worky-navigation-menu-content">
                            <button
                                onClick={() => {
                                    handleNewShiftClick();
                                    setShowQuickActionsMenu(false);
                                }}
                                className={`menu-item ${url.includes('/new-shift') ? 'active' : ''}`}
                            >
                                <Plus className="menu-icon" size={20} />
                                <span>New Shift</span>
                            </button>
                            <button
                                onClick={() => {
                                    handleAccountSettingsClick();
                                    setShowQuickActionsMenu(false);
                                }}
                                className={`menu-item ${url.includes('account') ? 'active' : ''}`}
                            >
                                <CircleUserRound className="menu-icon" size={20} />
                                <span>View Account</span>
                            </button>
                            <button
                                onClick={() => {
                                    handleAddNewContactClick();
                                    setShowQuickActionsMenu(false);
                                }}
                                className={`menu-item ${url.includes('/agency/create-new-contact') ? 'active' : ''}`}
                            >
                                <UserPlus className="menu-icon" size={20} />
                                <span>Add New Contact</span>
                            </button>
                            <button
                                onClick={() => {
                                    handleCreateTaskClick();
                                    setShowQuickActionsMenu(false);
                                }}
                                className="menu-item"
                            >
                                <SquarePen className="menu-icon" size={20} />
                                <span>Add New Task</span>
                            </button>
                        </div>
                    </div>
                )}
            </>
        )
    }

    // Helper component for rendering the client icon
    const ClientIcon = ({ corpDetails, isCollapsed }: { corpDetails: any, isCollapsed: boolean }) => {
        try {
            if (!corpDetails) return null;
            if (!corpDetails.corpIconUrl) return null;

            const iconUrl: string | null = corpDetails?.corpIconUrl || null;
            const iconClassName: string = isCollapsed
                ? 'worky-navigation-agency-icon worky-navigation-agency-icon-collapsed'
                : 'worky-navigation-agency-icon';

            if (iconUrl) {
                return (
                    <img
                        src={iconUrl}
                        alt="Client Icon"
                        className={iconClassName}
                        onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
                    />
                );
            }
            return null;
        } catch (err) {
            console.error('Error rendering client icon:', err);
            return null;
        }
    };


    const WorkyNavBrandingBottom = ({ corpDetails, isCollapsed, handleHomeClick }: { corpDetails: any, isCollapsed: boolean, handleHomeClick: () => void }) => {
        try {
            const hasIcon: boolean = !!(corpDetails?.corpIconUrl);
            return (
                <div className="worky-navigation-agency-branding">
                    {isCollapsed ? (
                        hasIcon ? (
                            <ClientIcon corpDetails={corpDetails} isCollapsed={isCollapsed} />
                        ) : (
                            null
                        )
                    ) : (
                        <div className="worky-navigation-agency-branding-expanded">
                            {hasIcon ? (
                                <ClientIcon corpDetails={corpDetails} isCollapsed={isCollapsed} />
                            ) : corpDetails && corpDetails.corpJobberDetails && corpDetails.corpJobberDetails.corpName ? (
                                <span className="worky-navigation-logo-bottom-text">{corpDetails.corpJobberDetails?.corpName}</span>
                            ) : null}
                        </div>
                    )}
                </div>
            );
        } catch (err) {
            console.error('Error rendering WorkyNavBranding:', err);
            return null;
        }
    };

    const WorkyNavigationDesktop = ({ isCollapsed, toggleSidebar }: { isCollapsed: boolean, toggleSidebar: () => void }) => {
        return (
            <>
                <div className={`worky-navigation-desktop ${isCollapsed ? 'collapsed' : ''}`}>
                    <div className={`worky-navigation-desktop-inner ${isCollapsed ? 'collapsed' : ''}`}>
                        {/* Clickable border area with sliding button */}
                        <div
                            className="worky-navigation-border-clickable"
                            onMouseMove={(e) => {
                                const button = e.currentTarget.querySelector('.worky-navigation-border-button') as HTMLElement;
                                if (button) {
                                    // Use clientY directly since button is positioned fixed
                                    button.style.top = `${e.clientY}px`;
                                    // Position horizontally at the border
                                    const sidebarWidth = isCollapsed ? 65 : 250;
                                    button.style.left = `${sidebarWidth}px`;
                                }
                            }}
                        >
                            <button
                                className="worky-navigation-border-button"
                                onClick={toggleSidebar}
                                aria-label="Toggle Sidebar"
                            >
                                {isCollapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
                            </button>
                        </div>

                        <div className="worky-navigation-logo">
                            {isCollapsed ? (<span className="worky-title">w</span>) : (<div style={{ display: "flex", alignItems: "center", justifyContent: "center", marginTop: 4 }}>
                                <span className="worky-title" style={{ fontSize: "35px" }}>worky</span>
                            </div>)}
                        </div>

                        <div className="worky-navigation-section" style={{ marginTop: isCollapsed ? "0px" : "0px" }}>
                            <div className="worky-navigation-section-header">
                                <span
                                    className="worky-navigation-section-title worky-navigation-section-toggle"
                                    onClick={toggleExecutionSection}
                                    aria-label="Toggle Execution Section"
                                >
                                    {isCollapsed ? NAV_SECTION_EXECUTION_TITLE_COLLAPSED : NAV_SECTION_EXECUTION_TITLE}
                                    {isExecutionSectionExpanded ? <ChevronDown size={isCollapsed ? 12 : 16} /> : <ChevronRight size={isCollapsed ? 12 : 16} />}
                                </span>
                            </div>
                            {isExecutionSectionExpanded && (
                                <div className="worky-navigation-menu-desktop">
                                    <Tooltip arrow title="Submit new shifts" placement="right">
                                        <button
                                            onClick={handleNewShiftClick}
                                            style={{ marginLeft: "0px", width: "100%" }}
                                            className={`menu-item ${url.includes('/new-shift') ? 'active' : ''}`}
                                        >
                                            <Plus size={17} />
                                            {!isCollapsed && <span>New Shift</span>}
                                        </button></Tooltip>
                                    <Tooltip arrow title="View and manage your teams shifts" placement="right">
                                        <button
                                            onClick={handlePlannedShiftsClick}
                                            style={{ marginLeft: "0px", width: "100%" }}
                                            className={`menu-item ${url.includes('/my-shifts') ? 'active' : ''}`}
                                        >
                                            <ChartGantt size={17} />
                                            {!isCollapsed && <span>Shift Management</span>}
                                        </button></Tooltip>
                                    <Tooltip arrow title="Workforce Management" placement="right">
                                        <button
                                            onClick={handleWorkforceClick}
                                            style={{ marginLeft: "0px", width: "100%" }}
                                            className={`menu-item ${url.includes('/agency/workforce') || url.includes('/agency/attributes') ? 'active' : ''}`}
                                        >
                                            <Users size={17} />
                                            {!isCollapsed && <span>Workforce</span>}
                                        </button></Tooltip>
                                    <Tooltip arrow title="Manage Client Configuration" placement="right">
                                        <button
                                            onClick={handleWorkyBillingClick}
                                            style={{ marginLeft: "0px", width: "100%" }}
                                            className={`menu-item ${url.includes('/agency/clients') || url.includes(WORKY_WEB_APP_PATHS.agencyCareProfileAttributes) ? 'active' : ''}`}
                                        >
                                            <BusinessIcon sx={{ fontSize: "17px" }} />
                                            {!isCollapsed && <span>Client Management</span>}
                                        </button></Tooltip>
                                    <Tooltip arrow title="Catalog item definitions for your agency" placement="right">
                                        <button
                                            onClick={handleCatalogItemsClick}
                                            style={{ marginLeft: "0px", width: "100%" }}
                                            className={`menu-item ${url.includes('/agency/catalog-items') ? 'active' : ''}`}
                                        >
                                            <Package size={17} />
                                            {!isCollapsed && <span>Catalog items</span>}
                                        </button></Tooltip>
                                    <Tooltip arrow title="Trust Vault" placement="right">
                                        <button
                                            onClick={handleTasksClick}
                                            style={{ marginLeft: "0px", width: "100%" }}
                                            className={`menu-item ${url.includes(WORKY_WEB_APP_PATHS.agencyTasks) ? 'active' : ''}`}
                                        >
                                            <ClipboardList size={17} />
                                            {!isCollapsed && <span>Trust Vault</span>}
                                        </button></Tooltip>
                                </div>
                            )}
                        </div>

                        <div className="worky-navigation-section">
                            <div className="worky-navigation-section-header">
                                <span
                                    className="worky-navigation-section-title worky-navigation-section-toggle"
                                    onClick={toggleFinanceSection}
                                    aria-label="Toggle Billing and Pay Section"
                                >
                                    {isCollapsed ? NAV_SECTION_BILLING_PAY_TITLE_COLLAPSED : NAV_SECTION_BILLING_PAY_TITLE}
                                    {isFinanceSectionExpanded ? <ChevronDown size={isCollapsed ? 12 : 16} /> : <ChevronRight size={isCollapsed ? 12 : 16} />}
                                </span>
                            </div>
                            {isFinanceSectionExpanded && (
                                <div className="worky-navigation-menu-desktop">
                                    <Tooltip arrow title="Workforce Payments" placement="right">
                                        <button
                                            onClick={handleWorkyPayClick}
                                            style={{ marginLeft: "0px", width: "100%" }}
                                            className={`menu-item ${url.includes('/agency/worky-pay') ? 'active' : ''}`}
                                        >
                                            <CreditCard size={17} />
                                            {!isCollapsed && <span>Worky Pay</span>}
                                        </button></Tooltip>
                                    <Tooltip arrow title="Client Invoices" placement="right">
                                        <button
                                            onClick={handleClientInvoicesClick}
                                            style={{ marginLeft: "0px", width: "100%" }}
                                            className={`menu-item ${url.includes('/agency/client-invoices') ? 'active' : ''}`}
                                        >
                                            <FileText size={17} />
                                            {!isCollapsed && <span>Client Invoices</span>}
                                        </button></Tooltip>
                                    <Tooltip arrow title="Custom Reports" placement="right">
                                        <button
                                            onClick={handleBillingPayReportingClick}
                                            style={{ marginLeft: "0px", width: "100%" }}
                                            className={`menu-item ${isCustomReportsNavPathActive(url) ? 'active' : ''}`}
                                        >
                                            <BarChart3 size={17} />
                                            {!isCollapsed && <span>Reporting</span>}
                                        </button></Tooltip>
                                </div>
                            )}
                        </div>

                        <div className="worky-navigation-section">
                            <div className="worky-navigation-section-header">
                                <span className="worky-navigation-section-title worky-navigation-section-toggle" onClick={toggleSalesSection} aria-label="Toggle Sales Section">
                                    {isCollapsed ? 'SALE' : 'SALES'}
                                    {isSalesSectionExpanded ? <ChevronDown size={isCollapsed ? 12 : 16} /> : <ChevronRight size={isCollapsed ? 12 : 16} />}
                                </span>
                            </div>
                            {isSalesSectionExpanded && (
                                <div className="worky-navigation-menu-desktop">
                                    <Tooltip arrow title="Sales Homepage" placement="right">
                                        <button
                                            onClick={handleWorkyLeadsHomeClick}
                                            className={`menu-item ${url.includes('/agency/sales/home') ? 'active' : ''}`}
                                        >
                                            <Home size={17} />
                                            {!isCollapsed && <span>Sales Home</span>}
                                        </button></Tooltip>

                                    <Tooltip arrow title="Leads Pro" placement="right">
                                        <button
                                            onClick={handleWorkyLeadsClick}
                                            style={{ marginLeft: "0px", width: "100%" }}
                                            className={`menu-item ${url.includes('/agency/leads') ? 'active' : ''}`}
                                        >
                                            <CurrencyExchangeIcon sx={{ fontSize: "17px" }} />
                                            {!isCollapsed && <span>Leads Pro</span>}
                                        </button></Tooltip>
                                    <Tooltip arrow title="Add New Contact" placement="right">
                                        <button
                                            onClick={handleAddNewContactClick}
                                            style={{ marginLeft: "0px", width: "100%" }}
                                            className={`menu-item ${url.includes('/agency/create-new-contact') ? 'active' : ''}`}
                                        >
                                            <UserPlus size={17} />
                                            {!isCollapsed && <span>Add New Contact</span>}
                                        </button></Tooltip>
                                    <Tooltip arrow title="Coming Soon!" placement="right">
                                        <button
                                            style={{ marginLeft: "0px", width: "100%" }}
                                            className={`menu-item ${url.includes('/agency/marketplace') ? 'active' : ''}`}
                                        >
                                            <Store size={17} />
                                            {!isCollapsed && <span>Marketplace</span>}
                                        </button></Tooltip>

                                    <Tooltip arrow title="Coming Soon!" placement="right">
                                        <button
                                            className="menu-item worky-ai-menu-item"
                                            style={{ marginLeft: "0px", width: "100%" }}
                                        >
                                            <Sparkles size={17} />
                                            {!isCollapsed && <span>Worky AI</span>}
                                        </button></Tooltip>

                                    <Tooltip arrow title="View Tasks" placement="right">
                                        <button
                                            onClick={handleViewMyTasksClick}
                                            style={{ marginLeft: "0px", width: "100%" }}
                                            className={`menu-item ${url.includes('/agency/view-tasks') ? 'active' : ''}`}
                                        >
                                            <CheckSquare size={17} />
                                            {!isCollapsed && <span>View Tasks</span>}
                                        </button></Tooltip>

                                </div>
                            )}
                        </div>


                        <div className="worky-navigation-logo-bottom">
                            <div className={`worky-navigation-sidebar-footer ${isCollapsed ? "collapsed" : ""}`}>
                                <WorkyNavBrandingBottom corpDetails={corpDetails} isCollapsed={isCollapsed} handleHomeClick={handleHomeClick} />

                                <div
                                    className="worky-navigation-sidebar-user"
                                    onClick={handleProfileIconClick}
                                    role="button"
                                    tabIndex={0}
                                    aria-label={`${localFirstName} ${localLastName}`}
                                    onKeyDown={(event: React.KeyboardEvent<HTMLDivElement>) => {
                                        if (event.key === "Enter" || event.key === " ") {
                                            event.preventDefault();
                                            handleProfileIconClick();
                                        }
                                    }}
                                >
                                    <span className="worky-navigation-sidebar-user-name">
                                        {isCollapsed
                                            ? (localFirstName?.[0] && localLastName?.[0] ? `${localFirstName[0]}${localLastName[0]}` : `${localFirstName} ${localLastName}`.trim())
                                            : `${localFirstName} ${localLastName}`.trim()}
                                    </span>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            </>
        )
    }

    const accountMenuUserDisplayName: string = `${localFirstName} ${localLastName}`.trim();

    return (
        <>
            <StyleLinks />
            {isMobile ? (
                <>
                    <WorkyNavigationMobile />
                    <Modal
                        open={showCreateTaskPopper}
                        onClose={handleCreateTaskPopperClose}
                        aria-labelledby="create-worky-task-modal-title"
                        aria-describedby="create-worky-task-modal-description"
                        disableEnforceFocus={true}
                        disableAutoFocus={true}
                        disableRestoreFocus={true}
                        BackdropProps={{
                            style: {
                                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                            }
                        }}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <div style={{
                            backgroundColor: 'white',
                            borderRadius: '8px',
                            padding: '20px',
                            maxWidth: '90vw',
                            maxHeight: '90vh',
                            overflow: 'auto',
                            outline: 'none',
                        }}>
                            <CreateWorkyTasks
                                showModal={showCreateTaskPopper}
                                onClose={handleCreateTaskPopperClose}
                            />
                        </div>
                    </Modal>
                </>
            ) : (
                <WorkyNavigationDesktop isCollapsed={isCollapsed} toggleSidebar={toggleSidebar} />
            )}
            <Modal
                open={showAccountMenu}
                onClose={handleAccountMenuClose}
                aria-labelledby="worky-account-menu-modal-title"
                BackdropProps={{
                    style: {
                        backgroundColor: "rgba(0, 0, 0, 0.5)",
                    },
                }}
                style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <div className="worky-account-menu-modal">
                    <div className="worky-account-menu-modal-title" id="worky-account-menu-modal-title">
                        {accountMenuUserDisplayName}
                    </div>
                    <div className="worky-account-menu-modal-actions">
                        <button
                            type="button"
                            className="worky-account-menu-modal-action"
                            onClick={() => {
                                handleAccountMenuClose();
                                handleAccountSettingsClick();
                            }}
                        >
                            <Cog size={16} /> Account Settings
                        </button>
                        <button
                            type="button"
                            className="worky-account-menu-modal-action"
                            onClick={() => {
                                handleAccountMenuClose();
                                handleLogOutClick();
                            }}
                        >
                            <LogOut size={16} /> Log Out
                        </button>
                    </div>
                </div>
            </Modal>
        </>
    )
}

export { WorkyNavigation }